import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { setStoredRedirectTarget } from '../utils/authStorage';

const CONNECT_PATH = '/telegram/connect';
const TELEGRAM_BOT_USERNAME = 'shefagreen_bot';
const TELEGRAM_TME_LINK_PREFIX = `https://t.me/${TELEGRAM_BOT_USERNAME}?start=`;
const TELEGRAM_BOT_LINK = `https://t.me/${TELEGRAM_BOT_USERNAME}`;
const TELEGRAM_START_PAYLOAD_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function buildTelegramLinks(startToken) {
  const encodedStartToken = encodeURIComponent(startToken);
  const payloadValid = isValidTelegramStartToken(startToken);

  return {
    startUrl: `${TELEGRAM_TME_LINK_PREFIX}${encodedStartToken}`,
    tokenLength: startToken.length,
    payloadValid,
  };
}

function logTelegramOpenDiagnostic({ openAttempted, payloadLength = 0, payloadValid = false }) {
  console.info('telegram_connect_open', {
    link_type: 't_me_start',
    payload_length: payloadLength,
    payload_valid: Boolean(payloadValid),
    open_attempted: Boolean(openAttempted),
  });
}

function isValidTelegramStartToken(startToken) {
  return typeof startToken === 'string' && TELEGRAM_START_PAYLOAD_PATTERN.test(startToken);
}

function extractVerifiedTelegramStartToken(rawUrl) {
  const parsed = new URL(rawUrl);
  const queryEntries = Array.from(parsed.searchParams.keys());
  const startToken = parsed.searchParams.get('start') || '';
  const valid =
    parsed.protocol === 'https:' &&
    parsed.host === 't.me' &&
    parsed.pathname === `/${TELEGRAM_BOT_USERNAME}` &&
    queryEntries.length === 1 &&
    queryEntries[0] === 'start' &&
    isValidTelegramStartToken(startToken);

  if (!valid) {
    throw new Error('invalid_telegram_url_shape');
  }

  return startToken;
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return 'clipboard_api';
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.top = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  textArea.remove();
  return 'exec_command';
}

function getCurrentPath(location) {
  return `${location.pathname}${location.search}${location.hash}` || CONNECT_PATH;
}

export default function TelegramConnectPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, ready, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [telegramLinks, setTelegramLinks] = useState(null);
  const [telegramStartToken, setTelegramStartToken] = useState('');
  const [copyStatus, setCopyStatus] = useState('idle');
  const [connected, setConnected] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);
  const [widgetFailed, setWidgetFailed] = useState(false);
  const widgetContainerRef = useRef(null);
  const widgetMountedRef = useRef(false);
  const linkRequestPromiseRef = useRef(null);

  const redirectToLogin = useCallback(() => {
    const redirectTarget = getCurrentPath(location);
    setStoredRedirectTarget(redirectTarget);
    navigate(`/login?redirect=${encodeURIComponent(redirectTarget)}`, {
      replace: true,
      state: { from: redirectTarget },
    });
  }, [location, navigate]);

  const createTelegramLink = useCallback(async () => {
    if (telegramLinks) {
      return telegramLinks;
    }
    if (linkRequestPromiseRef.current) {
      return linkRequestPromiseRef.current;
    }

    if (!token) {
      redirectToLogin();
      return null;
    }

    setError('');
    setBusy(true);

    const requestPromise = (async () => {
      const res = await authFetch(
        '/api/user/telegram-link-token',
        {},
        { token, onUnauthorized: () => {} }
      );
      if (res.status === 401) {
        logout();
        redirectToLogin();
        return null;
      }
      if (res.status === 429) {
        setError(t('telegramConnect.rateLimit'));
        return null;
      }
      if (!res.ok) {
        setError(t('telegramConnect.signInAgain'));
        return null;
      }
      const data = await res.json();

      let startToken = data.telegramStartToken;
      if (!isValidTelegramStartToken(startToken)) {
        if (!data.telegramUrl || typeof data.telegramUrl !== 'string') {
          setError(t('telegramConnect.signInAgain'));
          return null;
        }

        try {
          startToken = extractVerifiedTelegramStartToken(data.telegramUrl);
        } catch {
          setError(t('telegramConnect.signInAgain'));
          return null;
        }
      }

      const links = buildTelegramLinks(startToken);
      setTelegramLinks(links);
      setTelegramStartToken(startToken);
      return links;
    })();

    linkRequestPromiseRef.current = requestPromise;
    try {
      return await requestPromise;
    } catch {
      setError(t('telegramConnect.signInAgain'));
      return null;
    } finally {
      linkRequestPromiseRef.current = null;
      setBusy(false);
    }
  }, [logout, redirectToLogin, t, telegramLinks, token]);

  const openTelegramLinks = useCallback((links) => {
    if (!links) {
      logTelegramOpenDiagnostic({ openAttempted: false });
      return;
    }

    logTelegramOpenDiagnostic({
      openAttempted: true,
      payloadLength: links.tokenLength,
      payloadValid: links.payloadValid,
    });

    window.location.href = links.startUrl;
  }, []);

  const openTelegram = useCallback(async () => {
    const links = await createTelegramLink();
    openTelegramLinks(links);
  }, [createTelegramLink, openTelegramLinks]);

  const copyConnectCode = useCallback(async () => {
    if (!telegramStartToken) return;
    const connectCode = `/start ${telegramStartToken}`;
    setCopyStatus('copying');
    try {
      await copyTextToClipboard(connectCode);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('idle');
      setError(t('telegramConnect.copyError'));
    }
  }, [telegramStartToken, t]);

  useEffect(() => {
    if (!ready) return;
    if (!token) {
      redirectToLogin();
    }
  }, [ready, redirectToLogin, token]);

  useEffect(() => {
    if (!ready || !token || connected || widgetMountedRef.current || !widgetContainerRef.current) {
      return undefined;
    }

    widgetMountedRef.current = true;
    setWidgetFailed(false);
    setWidgetReady(false);
    let disposed = false;
    window.__shefaTelegramLoginWidgetAuth = async (telegramUser) => {
      console.info('telegram_login_widget_started');
      setBusy(true);
      setError('');
      try {
        const res = await authFetch(
          '/api/user/telegram-login-widget',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(telegramUser),
          },
          {
            token,
            onUnauthorized: () => {},
          }
        );

        if (res.status === 401) {
          logout();
          redirectToLogin();
          return;
        }
        if (!res.ok) {
          console.info('telegram_login_widget_failed');
          setError(t('telegramConnect.signInAgain'));
          return;
        }

        console.info('telegram_login_widget_success');
        setConnected(true);
      } catch {
        console.info('telegram_login_widget_failed');
        setError(t('telegramConnect.signInAgain'));
      } finally {
        setBusy(false);
      }
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js';
    script.setAttribute('data-telegram-login', TELEGRAM_BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'window.__shefaTelegramLoginWidgetAuth(user)');
    script.onload = () => {
      window.setTimeout(() => {
        if (disposed) return;
        const rendered = Boolean(widgetContainerRef.current?.querySelector('iframe'));
        setWidgetReady(rendered);
        setWidgetFailed(!rendered);
        if (!rendered) {
          console.info('telegram_login_widget_failed');
        }
      }, 800);
    };
    script.onerror = () => {
      if (disposed) return;
      console.info('telegram_login_widget_failed');
      setWidgetFailed(true);
      setError(t('telegramConnect.widgetLoadError'));
    };
    widgetContainerRef.current.innerHTML = '';
    widgetContainerRef.current.appendChild(script);

    return () => {
      disposed = true;
      if (widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
      delete window.__shefaTelegramLoginWidgetAuth;
      widgetMountedRef.current = false;
    };
  }, [connected, logout, ready, redirectToLogin, t, token]);

  return (
    <div className="container py-5" style={{ maxWidth: 680 }}>
      <div className="card shadow-sm border-0">
        <div className="card-body p-4 p-md-5">
          <p className="text-uppercase text-muted small fw-semibold mb-2">
            {t('telegramConnect.eyebrow')}
          </p>
          <h1 className="h3 mb-3">{t('telegramConnect.title')}</h1>
          <p className="text-muted mb-4">{t('telegramConnect.description')}</p>

          {connected ? (
            <div className="alert alert-success" role="status">
              Telegram connected successfully
            </div>
          ) : null}

          {busy ? (
            <div className="alert alert-info d-flex align-items-center gap-2" role="status">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              <span>{t('telegramConnect.connecting')}</span>
            </div>
          ) : null}

          {error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : null}

          {connected ? (
            <div className="d-grid gap-2">
              <a
                className="btn btn-primary btn-lg d-inline-flex align-items-center justify-content-center gap-2"
                href={TELEGRAM_BOT_LINK}
                target="_blank"
                rel="noreferrer"
              >
                Open Telegram bot
              </a>
            </div>
          ) : (
            <>
              <div className="mb-2 fw-semibold">Connect with Telegram</div>
              <div
                ref={widgetContainerRef}
                className="d-flex align-items-center telegram-login-widget-slot"
                aria-busy={!widgetReady}
              />
              {!widgetReady && !widgetFailed ? (
                <div className="small text-muted mt-2" role="status">
                  {t('telegramConnect.widgetLoading')}
                </div>
              ) : null}
              {widgetFailed ? (
                <div className="alert alert-warning mt-3 mb-0" role="alert">
                  {t('telegramConnect.widgetLoadError')}
                </div>
              ) : null}
            </>
          )}

          {!connected ? (
            <details
              className="small text-muted mt-3 mb-0"
              onToggle={(event) => {
                if (event.currentTarget.open) createTelegramLink();
              }}
            >
              <summary>Having trouble connecting?</summary>
              <div className="d-grid gap-2 mt-2">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center gap-2"
                  onClick={openTelegram}
                  disabled={busy}
                >
                  {t('telegramConnect.openTelegramButton')}
                </button>
                {telegramLinks ? (
                  <code className="user-select-all">/start {telegramStartToken}</code>
                ) : null}
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center gap-2"
                  onClick={copyConnectCode}
                  disabled={!telegramStartToken || copyStatus === 'copying'}
                >
                  {copyStatus === 'copied'
                    ? t('telegramConnect.copyConnectCodeDone')
                    : t('telegramConnect.copyConnectCodeButton')}
                </button>
              </div>
            </details>
          ) : null}
        </div>
      </div>
    </div>
  );
}
